import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Flame } from "lucide-react";
import { scrollToSection } from "@/App";

export const ORDER_URL = "https://waiterman.ai/kababishrestaur/order";

const links = [
  { label: "Home", target: "#home", external: false },
  { label: "About Us", target: "#about", external: false },
  { label: "Menu", target: ORDER_URL, external: true },
  { label: "Gallery", target: "#gallery", external: false },
  { label: "Contact Us", target: "#contact", external: false },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.external) {
      window.open(link.target, "_blank", "noopener,noreferrer");
    } else {
      scrollToSection(link.target);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b backdrop-blur-xl ${
        scrolled
          ? "bg-[#0A1128]/90 border-white/10 shadow-[0_8px_40px_rgba(10,17,40,0.5)]"
          : "bg-[#0A1128]/60 border-white/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go({ target: "#home" })}
          className="flex items-center gap-3 group"
          aria-label="Kababish home"
        >
          <img
            src="/assets/kababish-logo.jpg"
            alt="Kababish Calamvale logo"
            className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/60 group-hover:ring-gold transition-[box-shadow] duration-300"
          />
          <span className="font-heading text-2xl font-semibold tracking-tight text-cream hidden sm:block">
            Kababish
            <span className="text-gold text-sm align-super ml-1 tracking-widest font-body font-light">CALAMVALE</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l)}
              className="relative text-sm tracking-wide text-cream/80 hover:text-gold transition-colors duration-300 font-body after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-gold after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
          <motion.a
            data-testid="nav-order-online-btn"
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -2, boxShadow: "0 10px 30px rgba(198,40,40,0.45)" }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 bg-spice text-cream text-sm font-semibold tracking-wide px-6 py-2.5 rounded-full"
          >
            <Flame size={15} />
            Order Online
          </motion.a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-cream p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <MenuIcon size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#0A1128]/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l, i) => (
                <motion.button
                  key={l.label}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  initial={{ x: -16, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(l)}
                  className="text-left font-heading text-2xl text-cream/90 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.a
                data-testid="nav-mobile-order-btn"
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                href={ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-2 bg-spice text-cream font-semibold px-6 py-3.5 rounded-full"
              >
                <Flame size={16} />
                Order Online
              </motion.a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
